import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuthStore } from '@/stores/authStore';

function initialsOf(first: string | undefined, last: string | undefined): string {
  const a = (first ?? '').trim().charAt(0).toUpperCase();
  const b = (last ?? '').trim().charAt(0).toUpperCase();
  return `${a}${b}` || 'H';
}

export default function UserMenu(): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const [open, setOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handler = (event: MouseEvent): void => {
      if (containerRef.current === null) {
        return;
      }
      if (!containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (user === null) {
    return null;
  }

  const handleLogout = async (): Promise<void> => {
    setOpen(false);
    await logout();
    navigate('/login', { replace: true });
  };

  const initials = initialsOf(user.first_name, user.last_name);
  const fullName = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-3 rounded-xl border border-line bg-white px-3 py-2 transition hover:border-gold-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
      >
        <span
          aria-hidden
          className="grid h-9 w-9 place-items-center rounded-full bg-gold-500 font-display text-sm font-bold text-navy-700"
        >
          {initials}
        </span>
        <span className="hidden text-left sm:block">
          <span className="block font-display text-xs font-bold text-navy-700">
            {fullName}
          </span>
          <span className="block font-sans text-[11px] text-sub">{user.role}</span>
        </span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-12 z-50 w-56 overflow-hidden rounded-xl border border-line bg-white shadow-lift"
        >
          <div className="border-b border-line px-4 py-3">
            <p className="font-display text-sm font-bold text-navy-700">{fullName}</p>
            <p className="font-sans text-xs text-sub">{user.email}</p>
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              navigate('/profile');
            }}
            className="block w-full px-4 py-2.5 text-left font-sans text-sm text-ink hover:bg-pearl focus:outline-none focus-visible:bg-pearl"
          >
            Mon profil
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              void handleLogout();
            }}
            className="block w-full border-t border-line px-4 py-2.5 text-left font-sans text-sm text-danger hover:bg-danger/5 focus:outline-none focus-visible:bg-danger/5"
          >
            Se déconnecter
          </button>
        </div>
      )}
    </div>
  );
}
